// Data Converter connection prefill + persistence.
//
// This module contains NO connection values — only generic plumbing. Actual
// values live in `src/devConnections.local.ts`, which is gitignored and never
// committed (so credentials never enter source control). On top of that seed
// file, any edits made in the app are remembered in localStorage.
//
// Resolution order for a form's initial value:
//   1. localStorage (edits you've made in the app)
//   2. the local seed file (devConnections.local.ts), if present
//   3. the hard-coded empty fallback passed by the caller

// The seed file is optional and machine-local. `import.meta.glob` resolves to
// an empty object when the file is absent, so the build never breaks for
// teammates who don't have it.
const localSeedModules = import.meta.glob("../devConnections.local.ts", {
  eager: true,
}) as Record<
  string,
  { DEV_CONNECTIONS?: Record<string, Record<string, unknown>> }
>;

const SEED_DEFAULTS: Record<string, Record<string, unknown>> = Object.values(
  localSeedModules,
)[0]?.DEV_CONNECTIONS ?? {};

const LS_PREFIX = "ztd.dataconverter.conn.";

/** Initial value for a connection form: localStorage → seed file → fallback. */
export function loadConn<T extends object>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw) return { ...fallback, ...(JSON.parse(raw) as Partial<T>) };
  } catch {
    // corrupt/disabled storage — fall through to seed/fallback
  }
  const seed = SEED_DEFAULTS[key];
  if (seed) return { ...fallback, ...(seed as Partial<T>) };
  return fallback;
}

/** Persist a connection form's current value so it prefills next time. */
export function saveConn<T extends object>(key: string, value: T | null): void {
  if (value == null) return;
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch {
    // storage full/disabled — non-fatal, we just won't remember this edit
  }
}

/** Forget a remembered connection edit, so the next loadConn() falls back to
 * the seed file / caller-supplied default instead of a stale localStorage value. */
export function clearConn(key: string): void {
  try {
    localStorage.removeItem(LS_PREFIX + key);
  } catch {
    // storage disabled — nothing to clear
  }
}
